class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) {
      this.resetScrollPosition();
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.resetScrollPosition();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        this.resetScrollPosition({ mobileOnly: true, settle: true });
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  resetScrollPosition({ mobileOnly = false, settle = false } = {}) {
    if (mobileOnly && !window.matchMedia('(max-width: 749px)').matches) return;

    const reset = () => {
      const drawerInner = this.querySelector('.drawer__inner');
      const drawerBody = this.querySelector('.drawer__body');
      const drawerItems = this.querySelector('cart-drawer-items');
      if (drawerInner) drawerInner.scrollTop = 0;
      if (drawerBody) drawerBody.scrollTop = 0;
      if (drawerItems) drawerItems.scrollTop = 0;
    };

    reset();
    requestAnimationFrame(() => {
      reset();
      requestAnimationFrame(reset);
    });

    if (settle) {
      this.scrollResetTimers?.forEach(clearTimeout);
      this.scrollResetTimers = [60, 180, 320].map((delay) => setTimeout(reset, delay));
    }
  }

  close() {
    this.scrollResetTimers?.forEach(clearTimeout);
    this.scrollResetTimers = [];
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.resetScrollPosition();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

const landingCartFormSelector =
  '[data-aloha-bogo-form], [data-iqbar-bogo-form], [data-ultima-bogo-form], [data-thorne-bogo-form], [data-codeage-form]';

const submitLandingCartToDrawer = async (event, form, clickedButton) => {
    const submitButton = clickedButton || event.submitter || form.querySelector('button[type="submit"]');
    if (!submitButton || submitButton.disabled) return;

    const isSingleBundle = form.matches('[data-thorne-bogo-form], [data-codeage-form]');
    const firstInput = form.querySelector(
      '[data-aloha-id-one], [data-bogo-variant-one], [data-codeage-variant]',
    );
    const secondInput = form.querySelector('[data-aloha-id-two], [data-bogo-variant-two]');
    const firstId = Number(firstInput?.value);
    const secondId = Number(secondInput?.value);

    if (!firstId || (!isSingleBundle && !secondId)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    submitButton.disabled = true;

    const items = isSingleBundle
      ? [{ id: firstId, quantity: 1 }]
      : firstId === secondId
        ? [{ id: firstId, quantity: 2 }]
        : [
            { id: firstId, quantity: 1 },
            { id: secondId, quantity: 1 },
          ];

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const base = window.Shopify?.routes?.root || '/';
      const sections = cartDrawer
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];
      const response = await fetch(`${base}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();

      if (!response.ok) throw new Error(result.description || 'Unable to add these items.');
      if (!cartDrawer) throw new Error('Cart preview is unavailable.');

      const sectionsUrl = new URL(base, window.location.origin);
      sectionsUrl.searchParams.set('sections', sections.join(','));
      sectionsUrl.searchParams.set('_', Date.now().toString());
      const sectionsResponse = await fetch(sectionsUrl.toString(), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      const freshSections = await sectionsResponse.json();

      if (!sectionsResponse.ok || !freshSections['cart-drawer']) {
        throw new Error('Cart preview is unavailable.');
      }

      cartDrawer.renderContents({ ...result, sections: freshSections });
    } catch (error) {
      const errorMessage = form.querySelector('[data-aloha-error], [data-bogo-error], [data-codeage-error]');
      if (errorMessage) {
        errorMessage.textContent = error.message || 'Unable to add these items. Please try again.';
        errorMessage.hidden = false;
      }
      submitButton.disabled = false;
    }
};

document.addEventListener(
  'click',
  (event) => {
    const submitButton = event.target.closest(`${landingCartFormSelector} button[type="submit"]`);
    const form = submitButton?.closest(landingCartFormSelector);
    if (!form) return;
    submitLandingCartToDrawer(event, form, submitButton);
  },
  true,
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest(landingCartFormSelector);
    if (!form) return;
    submitLandingCartToDrawer(event, form);
  },
  true,
);

document.addEventListener('change', (event) => {
  const terms = event.target.closest('[data-cart-terms]');
  if (!terms) return;
  const drawer = terms.closest('cart-drawer');
  const checkout = drawer?.querySelector('#CartDrawer-Checkout');
  const error = drawer?.querySelector('.vn-cart-terms__error');
  if (checkout) checkout.disabled = !terms.checked;
  if (error && terms.checked) error.hidden = true;
});

document.addEventListener('click', async (event) => {
  const discountButton = event.target.closest('[data-cart-discount-apply]');
  if (discountButton) {
    const input = discountButton.closest('.vn-cart-discount__form')?.querySelector('[data-cart-discount-input]');
    const code = input?.value.trim();
    if (!code || discountButton.disabled) return;

    const cartDrawer = discountButton.closest('cart-drawer');
    if (!cartDrawer?.renderContents || !cartDrawer?.getSectionsToRender) return;

    discountButton.disabled = true;
    discountButton.setAttribute('aria-busy', 'true');
    input.setCustomValidity('');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({
          discount: code,
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || 'Unable to apply this discount code.');
      }

      if (!state.sections?.['cart-drawer']) {
        throw new Error('Unable to refresh the cart preview.');
      }

      cartDrawer.renderContents(state);
    } catch (error) {
      input.setCustomValidity(error.message || 'Unable to apply this discount code.');
      input.reportValidity();
    } finally {
      discountButton.disabled = false;
      discountButton.removeAttribute('aria-busy');
    }
    return;
  }

  const checkout = event.target.closest('#CartDrawer-Checkout');
  if (!checkout) return;
  const drawer = checkout.closest('cart-drawer');
  const terms = drawer?.querySelector('[data-cart-terms]');
  const error = drawer?.querySelector('.vn-cart-terms__error');
  if (terms && !terms.checked) {
    event.preventDefault();
    if (error) error.hidden = false;
    terms.focus();
  }
});

class VnAlohaExtra extends HTMLElement {
  static busy = false;
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.addEventListener('change', (event) => event.stopPropagation());
    this.querySelector('[data-extra-add]').addEventListener('click', () => this.addPair());
    if ([...this.querySelectorAll('select')].some((select) => !select.options.length)) this.hidden = true;
  }
  async addPair() {
    if (VnAlohaExtra.busy) return;
    const drawer = this.closest('cart-drawer');
    if (!drawer?.renderContents) return;
    const button = this.querySelector('[data-extra-add]');
    const error = this.querySelector('[role="alert"]');
    const selects = [...this.querySelectorAll('[data-extra-flavor]')];
    const ids = selects.map((select) => Number(select.value));
    if (ids.some((id) => !Number.isSafeInteger(id) || id <= 0)) return;
    VnAlohaExtra.busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    selects.forEach((select) => select.disabled = true);
    error.hidden = true;
    let added = false;
    try {
      if (typeof CartItems !== 'undefined' && CartItems.pendingQuantityUpdate) await CartItems.pendingQuantityUpdate;
      const base = window.Shopify?.routes?.root || '/';
      const beforeResponse = await fetch(base + 'cart.js', {cache:'no-store', headers:{Accept:'application/json'}});
      if (!beforeResponse.ok) throw new Error(this.dataset.error);
      const before = await beforeResponse.json();
      const lines = before.items.filter((item) => String(item.product_id) === this.dataset.productId);
      if (before.currency !== 'USD' || lines.reduce((sum,item) => sum + item.quantity,0) !== 2 || lines.reduce((sum,item) => sum + item.final_line_price,0) !== Number(this.dataset.requiredTotal || 3999)) throw new Error(this.dataset.changed);
      const sections = drawer.getSectionsToRender().map((section) => section.id);
      const items = ids.map((id,index) => ({id, quantity:1, properties:{_vitanova_extra_pair:String(index + 1)}}));
      const response = await fetch(base + 'cart/add.js', {
        method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({items,sections,sections_url:window.location.pathname})
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
      added = true;
      let fresh = result.sections;
      if (!sections.every((id) => fresh?.[id])) {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', sections.join(','));
        const refresh = await fetch(url, {cache:'no-store', headers:{Accept:'application/json'}});
        if (!refresh.ok) throw new Error(this.dataset.added);
        fresh = await refresh.json();
      }
      if (!sections.every((id) => fresh?.[id])) throw new Error(this.dataset.added);
      drawer.renderContents({...result,sections:fresh});
      const checkout = drawer.querySelector('#CartDrawer-Checkout');
      if (checkout) checkout.focus({preventScroll:true});
    } catch (exception) {
      error.textContent = added ? this.dataset.added : (exception.message || this.dataset.error);
      error.hidden = false;
      if (!added) {
        button.disabled = false;
        selects.forEach((select) => select.disabled = false);
      }
    } finally {
      button.removeAttribute('aria-busy');
      VnAlohaExtra.busy = false;
    }
  }
}
if (!customElements.get('vn-aloha-extra')) customElements.define('vn-aloha-extra', VnAlohaExtra);
